import { useReducer, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { unifiedApiService } from '../services/unified';
import { TokenManager } from '../utils/auth';
import { AuthContext } from './AuthContextDefinition';
import { analytics } from '../utils/analytics';
import type { AuthState, AuthContextType, User } from '../types/auth';

// Auth actions
type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; isAdmin: boolean } }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_USER'; payload: { user: User; isAdmin: boolean } };

// Initial state
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isAdmin: false,
  loading: true, // Start with loading true to check existing token
  error: null,
};

// Auth reducer
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        isAdmin: action.payload.isAdmin,
        loading: false,
        error: null,
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isAdmin: false,
        loading: false,
        error: action.payload,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isAdmin: false,
        loading: false,
        error: null,
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    case 'SET_USER':
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        isAdmin: action.payload.isAdmin,
        loading: false,
        error: null,
      };
    default:
      return state;
  }
}

// Auth provider props
interface AuthProviderProps {
  children: ReactNode;
}

// Auth provider component
export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check for existing token on mount
  useEffect(() => {
    const initializeAuth = async () => {
      const token = TokenManager.getToken();
      
      if (token && TokenManager.isValidToken(token)) {
        try {
          dispatch({ type: 'SET_LOADING', payload: true });
          const userData = await unifiedApiService.auth.getCurrentUser();
          
          // Handle backend response structure: {is_admin: boolean, user: User}
          const actualUser = (userData as any).user || userData;
          const isAdmin = (userData as any).is_admin || userData.is_admin || false;
          
          dispatch({
            type: 'SET_USER',
            payload: {
              user: actualUser as User,
              isAdmin,
            },
          });
        } catch {
          TokenManager.clearAll();
          dispatch({ type: 'LOGOUT' });
        }
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    initializeAuth();
  }, []);

  // Login function
  const login = useCallback(async (idToken: string): Promise<void> => {
    try {
      dispatch({ type: 'LOGIN_START' });
      
      const response = await unifiedApiService.auth.login(idToken);
      console.log('AuthContext - Login response:', response);
      
      // Check if this is a new user registration
      const isNewUser = (response as any).is_new_user || false;
      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user: response.user,
          isAdmin: false, // Will be determined by profile fetch
        },
      });

      // Fetch full user profile to determine admin status
      try {
        const userData = await unifiedApiService.auth.getCurrentUser();
        console.log('AuthContext - getCurrentUser response:', userData);
        
        // Handle backend response structure: {is_admin: boolean, user: User}
        const actualUser = (userData as any).user || userData;
        const isAdmin = (userData as any).is_admin || userData.is_admin || false;
        
        dispatch({
          type: 'SET_USER',
          payload: {
            user: actualUser as User,
            isAdmin,
          },
        });

        // Track user registration if this is a new user
        if (isNewUser) {
          analytics.trackEvent({
            action: 'user_registered',
            category: 'User',
            label: 'Google OAuth',
          });
        }
      } catch (error) {
        console.error('AuthContext - Failed to get current user:', error);
        // Continue with basic user data
      }
    } catch (error) {
      const err = error as Error;
      const errorMessage = err.message || 'Login failed. Please try again.';
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      throw error;
    }
  }, []);

  // Logout function
  const logout = useCallback(async (): Promise<void> => {
    try {
      await unifiedApiService.auth.logout();
    } catch {
      // Continue with logout even if request fails
    } finally {
      dispatch({ type: 'LOGOUT' });
    }
  }, []);

  // Refresh token function
  const refreshToken = useCallback(async (): Promise<void> => {
    try {
      await unifiedApiService.auth.refreshToken();
      
      // Fetch updated user data
      const userData = await unifiedApiService.auth.getCurrentUser();
      
      // Handle backend response structure: {is_admin: boolean, user: User}
      const actualUser = (userData as any).user || userData;
      const isAdmin = (userData as any).is_admin || userData.is_admin || false;
      
      dispatch({
        type: 'SET_USER',
        payload: {
          user: actualUser as User,
          isAdmin,
        },
      });
    } catch (error) {
      dispatch({ type: 'LOGOUT' });
      throw error;
    }
  }, []);

  // Update user function
  const updateUser = useCallback((userData: Partial<User>): void => {
    if (state.user) {
      const updatedUser = { ...state.user, ...userData };
      dispatch({
        type: 'SET_USER',
        payload: {
          user: updatedUser,
          isAdmin: state.isAdmin,
        },
      });
    }
  }, [state.user, state.isAdmin]);

  // Clear error function
  const clearError = useCallback((): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  // Debug: Log state
  console.log('AuthContext - Current state:', state);
  console.log('AuthContext - state.user:', state.user);

  // Context value
  const contextValue: AuthContextType = {
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isAdmin: state.isAdmin,
    loading: state.loading,
    error: state.error,
    login,
    logout,
    refreshToken,
    updateUser,
    clearError,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

